import { useState } from "react";
import useSWR from "swr";
import PageLayout from "layouts/PageLayout";
import CalendarPicker from "components/CalendarPicker/CalendarPicker";
import CustomButton from "components/CustomButtons/Button.js";
import Table from "components/ReactTable/ReactTableV2";
import { makeStyles } from "@material-ui/core/styles";
import { Grid, Box, Button, Typography, Popover, Card } from "@material-ui/core";
import { DateRange as DateRangeIcon } from "components/MaterialSymbol/MaterialIcons";
import { GetApp as GetAppIcon } from "components/MaterialSymbol/MaterialIcons";
import MultiSelectCheckbox from "components/MultiSelectCheckbox/MultiSelectCheckbox";
import ReportService from "services/ReportService";
import useDealersSwr from "hooks/useDealersSwr";

const useStyles = makeStyles({
    customTitle: {
        fontSize: 12,
        fontWeight: "bold",
    },
    customDetail: {
        fontSize: 11,
    },
});

const DealerSalesSummaryReport = () => {
    const classes = useStyles();
    const [customDateRange, setCustomDateRange] = useState({ startDate: "", endDate: "" });
    const [anchorEl, setAnchorEl] = useState(null);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const { dealers = [], isLoading: dealersIsLoading } = useDealersSwr();
    const [selectedDealers, setSelectedDealers] = useState([]);

    const { data, error } = useSWR(
        [
            "dealer-sales-summary",
            selectedDealers,
            customDateRange.startDate,
            customDateRange.endDate,
            pageIndex,
            pageSize,
        ],
        () =>
            ReportService.fetchDealerSalesSummary(
                selectedDealers,
                customDateRange.startDate,
                customDateRange.endDate,
                pageIndex,
                pageSize
            ).then((res) => res.data)
    );

    console.log("pageIndex", pageIndex);
    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const columns = [
        {
            Header: "Dealer",
            accessor: "dealer",
        },
        {
            Header: "Dealer Code",
            accessor: (row) => row.dealerId || "N/A",
        },
        {
            Header: "# Orders MTD",
            accessor: (row) => row.ordersMtd ?? 0,
        },
        {
            Header: "Sales MTD (cost)",
            accessor: (row) =>
                row.salesMtd != null
                    ? Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                          row.salesMtd
                      )
                    : "$0.00",
        },
        {
            Header: "# Orders YTD",
            accessor: (row) => row.ordersYtd ?? 0,
        },
        {
            Header: "Sales YTD (cost)",
            accessor: (row) =>
                row.salesYtd != null
                    ? Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
                          row.salesYtd
                      )
                    : "$0.00",
        },
    ];

    return (
        <PageLayout>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <Box width={250}>
                            <MultiSelectCheckbox
                                label="Select Dealers"
                                options={dealers}
                                selectedValues={selectedDealers}
                                setSelectedValues={(values) => {
                                    setSelectedDealers(values);
                                    setPageIndex(0);
                                }}
                                getOptionLabel={(d) => d.company}
                                getOptionValue={(d) => d.id}
                            />
                        </Box>
                    </Grid>
                    <Grid item>
                        <CalendarPicker
                            fullWidth
                            label="From date"
                            date={customDateRange.startDate}
                            setDate={(startDate) =>
                                setCustomDateRange((prev) => ({ ...prev, startDate }))
                            }
                        />
                    </Grid>
                    <Grid item>
                        <CalendarPicker
                            fullWidth
                            label="To date"
                            date={customDateRange.endDate}
                            setDate={(endDate) =>
                                setCustomDateRange((prev) => ({ ...prev, endDate }))
                            }
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            onClick={() => {
                                setCustomDateRange({ startDate: "", endDate: "" });
                                setSelectedDealers([]);
                                setPageIndex(0);
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<GetAppIcon />}
                            onClick={() =>
                                ReportService.exportDealerSalesSummaryCSV(
                                    selectedDealers,
                                    customDateRange.startDate,
                                    customDateRange.endDate
                                )
                            }
                        >
                            Export CSV
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            <Box mt={2}>
                <Card>
                    <Box p={2}>
                        <Table
                            columns={columns}
                            data={data?.data || []}
                            pageCount={Math.ceil((data?.total || 0) / pageSize)}
                            fetchData={({ pageIndex, pageSize }) => {
                                setPageIndex(pageIndex);
                                setPageSize(pageSize);
                            }}
                        />
                    </Box>
                </Card>
            </Box>
        </PageLayout>
    );
};

export default DealerSalesSummaryReport;
